import values from 'lodash/values'
import find from 'lodash/find'
import get from 'lodash/get'
import isNumber from 'lodash/isNumber'
import { State } from 'interfaces/state'
import {
  FeedUserUid,
  FeedDataState,
  PostUid,
  SelectableFeedFilter,
  CommentUid,
  FeedFilterQuery,
  WithPostUid,
  FilterPostsLoading,
  FeedUser,
  WithUserUid,
  FeedUserResource,
  FeedTagResource,
  TagsOrdering,
  SuggestTag,
  SuggestUser,
  POST_TYPE_SOCIAL_INSIGHT,
  Post,
  FeedTag,
} from 'interfaces/feed'
import { createSelector, defaultMemoize } from 'reselect'
import { getItemFromCollection, getItemsFromCollection, filterCollection, setItemInCollection, getItemsByKey } from 'helpers/normalizeHelpers'
import { createOwnPropsSelector, memberDataSelector } from 'selectors/globalSelectors'
import { relationsSelector } from 'selectors/peergroupSelectors'
import { getFeedFilterPeergroupQuery, serializeFeedFilterQuery } from 'helpers/feedHelpers'
import { FeedPostListOwnProps } from 'components/features/socialFeed/FeedPostList/FeedPostList'
import { SLUGS } from 'constants/peergroups'
import { YOUR_POSTS_FILTER, FEEDGROUPS_FILTERS, FEED_TRENDING_TAGS_LIMIT, FEED_POPULAR_TAGS_LIMIT } from 'constants/feed'
import { CursorPaginatable } from 'interfaces/table'
import { PeergroupRelation, PeergroupRelationCollection } from 'interfaces/peergroups'
import { Collection } from 'interfaces'
import intersection from 'lodash/intersection'
import { encodeUrlParams } from 'helpers/utilsHelpers'
import { WithRequestDetails } from 'interfaces/ajax/base'
import { getFormValues } from 'redux-form'
import { shallowEqual } from 'tangle-shared'

type FeedFilterProps = {
  filter: SelectableFeedFilter | null
}

export const feedAppSelector = (state: State) => state.app.feed
export const feedDataSelector = (state: State) => state.data.feed

export const createIsPostEditingSelector = () => {
  return createSelector(
    feedAppSelector,
    createOwnPropsSelector<WithPostUid>(),
    (feed, { postUid }) => Boolean(feed.editingPostsIds[postUid])
  )
}

export const createAuthorFollowingLoadingSelector = () => {
  return createSelector(
    feedAppSelector,
    createOwnPropsSelector<WithUserUid>(),
    (feed, { userUid }) => Boolean(feed.authorFollowingLoadingIds[userUid])
  )
}

export const filterRandomRelationStateSelector = createSelector(
  feedDataSelector,
  (data) => data.randomFeedRelation,
)

export const filterRandomRelationSelector = createSelector(
  filterRandomRelationStateSelector,
  (state) => state.data ? state.data.relation : null,
)

export const feedRelationsSelector = createSelector(
  relationsSelector,
  filterRandomRelationSelector,
  (relations, feedRandomRelation): Collection<PeergroupRelation, 'id'> => {
    const filteredRelations = filterCollection<PeergroupRelation, 'id'>(
      (relation) => relation.slug !== SLUGS.random,
      relations,
    )

    return feedRandomRelation
      ? setItemInCollection(filteredRelations, feedRandomRelation, 'id')
      : filteredRelations
  },
)

export const filterRelationsSelector = createSelector(
  feedRelationsSelector,
  (relations) =>
    getItemsFromCollection(relations)
      .reduce<PeergroupRelation[]>((acc, relation) => {
        if (relation.is_preset) {
          return acc
        }

        acc.push(relation)
        return acc
      }, []),
)

// const defaultFilterSelector = createSelector(
//   peersRelationIdSelector,
//   (relationId): SelectableFeedFilter | null => {
//     if (relationId === null) {
//       return null
//     }

//     return {
//       type: 'peergroup',
//       relationId,
//     }
//   },
// )

const DEFAULT_FILTER: SelectableFeedFilter = {
  type: 'feedgroup',
  slug: 'everyone',
}

export const feedSelectedFilterSelector = createSelector(
  feedAppSelector,
  ({ userSelectedFilter }) => userSelectedFilter || DEFAULT_FILTER
)

export const feedSelectedGroupSelector = createSelector(
  feedSelectedFilterSelector,
  feedRelationsSelector,
  (selectedFilter, relations) => {
    if (!selectedFilter) {
      return null
    }

    const relation = selectedFilter.type === 'peergroup' ? getItemFromCollection(relations, selectedFilter.relationId) : null

    return {
      ...selectedFilter,
      relation,
    }
  }
)

const ownPropsResourceSelector = (_state: State, ownProps: FeedPostListOwnProps) => !!ownProps ?
  ownProps.resource : null

export const feedResourceQuerySelector = createSelector(
  ownPropsResourceSelector,
  (resource): FeedFilterQuery | null => {
    if (!resource) {
      return null
    }

    if (resource.type === 'user') {
      return {
        filter_by: 'user',
        username: resource.name,
      }
    }

    if (resource.type === 'tag') {
      return {
        filter_by: 'tag',
        name: resource.name,
      }
    }

    if (resource.type === 'post') {
      return {
        filter_by: 'post',
        uid: resource.postUid,
      }
    }

    return null
  }
)


export const feedPostsByFilterSelector = (state: State) => state.data.feed.postsByFilter

export const feedQuerySelector = (state: State, ownProps: FeedPostListOwnProps) => {
  const resource = feedResourceQuerySelector(state, ownProps)
  if (!resource) {
    const filter = feedSelectedFilterSelector(state)
    return helpSelectedFeedQuerySelector(state, { filter })
  }

  return resource
}


export const feedFiltersPostsLoadingSelector = createSelector(
  feedQuerySelector,
  feedPostsByFilterSelector,
  (query, postUidsByFilter): FilterPostsLoading | null => {
    if (!query) {
      return null
    }

    const stringifiedQuery = serializeFeedFilterQuery(query)
    const postsState = postUidsByFilter[stringifiedQuery]
    return postsState ? postsState.data : null
  }
)

export const feedPostUidsPaginatableBySelectedFilterSelector = createSelector(
  feedQuerySelector,
  feedPostsByFilterSelector,
  (query, postUidsByFilter): CursorPaginatable<PostUid> | null => {
    if (!query) {
      return null
    }

    const stringifiedQuery = serializeFeedFilterQuery(query)
    const postsState = postUidsByFilter[stringifiedQuery]
    return postsState ? postsState.data.pagination : null
  }
)


const feedUsersSelector = createSelector(
  feedDataSelector,
  ({ users }): FeedDataState['users'] => users
)

const ownUserUidSelector = (_state: State, ownProps: FeedUserUidOwnProps) => ownProps.userUid

export const createFeedUserSelector = () => {
  return createSelector(
    feedUsersSelector,
    ownUserUidSelector,
    (users, uid) => {
      if (!users) {
        return null
      }
      return getItemFromCollection<FeedUser, 'uid'>(users, uid)
    }
  )
}

export const feedUserSelector = createFeedUserSelector()

export const memberFeedInfoSelector = createSelector(
  memberDataSelector,
  feedUsersSelector,
  (data, users): FeedUser | null => {
    const uid = get(data, 'info.uid', null)
    if (!uid) {
      return null
    }

    return getItemFromCollection(users, uid)
  }
)


export type FeedUserUidOwnProps = {
  userUid: FeedUserUid
}

export const getQueryByFeedfilter = (filter: SelectableFeedFilter, memberFeed: FeedUser | null, relations: PeergroupRelationCollection): FeedFilterQuery | null => {
  if (!filter) {
    return {
      filter_by: 'feedgroup',
      slug: 'everyone',
    }
  }
  if (filter.type === 'feedgroup') {
    return {
      filter_by: 'feedgroup',
      slug: filter.slug,
    }
  }
  if (filter.type === 'your-posts' && memberFeed) {
    return {
      filter_by: 'user',
      uid: memberFeed.uid,
    }
  }
  if (filter.type === 'peergroup') {
    const relation = relations.byKey[filter.relationId]
    if (!relation) {
      return null
    }
    return getFeedFilterPeergroupQuery(relation)
  }
  return null
}

export const createFeedFilterQuerySelector = () => createSelector(
  (_state: State, ownProps: FeedFilterProps) => ownProps.filter,
  memberFeedInfoSelector,
  feedRelationsSelector,
  getQueryByFeedfilter,
)


export const createFilterHasNewPostsSelector = () => createSelector(
  createFeedFilterQuerySelector(),
  feedDataSelector,
  (query, feedData): boolean | null =>
    query ? (feedData.filterUpdates[serializeFeedFilterQuery(query)] || null) : null
)

const helpSelectedFeedFilterQuerySelector = createFeedFilterQuerySelector()
export const feedSelectedFilterQuerySelector = (state: State) => {
  const selectedFilter = feedSelectedFilterSelector(state)

  return selectedFilter ? helpSelectedFeedFilterQuerySelector(state, { filter: selectedFilter })
    : null
}

const helpSelectedFeedQuerySelector = createFeedFilterQuerySelector()

export const allFeedFiltersSelector = createSelector(
  feedRelationsSelector,
  (relations): SelectableFeedFilter[] =>
    getItemsFromCollection(relations)
      .filter((relation) => relation.slug !== SLUGS.everyone)
      .map((relation): SelectableFeedFilter => ({
        type: 'peergroup',
        relationId: relation.id,
      }))
      .concat(YOUR_POSTS_FILTER)
      .concat(values(FEEDGROUPS_FILTERS)),
)

export const allFeedFilterQueriesSelector = createSelector(
  allFeedFiltersSelector,
  memberFeedInfoSelector,
  relationsSelector,
  (allFilters, memberFeed, relations): FeedFilterQuery[] => {
    const queries: FeedFilterQuery[] = allFilters
      .map((filter) => getQueryByFeedfilter(filter, memberFeed, relations))
      .filter((query) => query !== null) as FeedFilterQuery[]

    return queries
  }
)

export const isAllFilterUpdatesLoadedSelector = (state: State) => {
  const filterQueries = allFeedFilterQueriesSelector(state)
  const feedData = feedDataSelector(state)
  return filterQueries.every((query) => Boolean(query && (typeof feedData.filterUpdates[serializeFeedFilterQuery(query)] === 'boolean')))
}

export const isCurrentlyFilteredPostsLoaded = createSelector(
  feedQuerySelector,
  (state: State) => state.data.feed.postsByFilter,
  (query, postsByFilter) => {
    return Boolean(query && postsByFilter[serializeFeedFilterQuery(query)])
  }
)

export const createFeedAuthorSelector = () => createSelector(
  feedUsersSelector,
  (_state: State, ownProps: FeedUserResource) => ownProps.name,
  (users, username): FeedUser | null => {
    return find(users.byKey, { username }) || null
  }
)

// NB: if user doesn't have `peergroup` prop, it means user had comes from posts list and we have to try to load this user separatly
//     if `peergroup` is null, it means null had comes from backend and we don't have to request this user again
export const createIsCurrentlySelectedAuthorLoaded = () => createSelector(
  createFeedAuthorSelector(),
  (state: State) => state.data.feed.users,
  (author, users) => Boolean(author && users.byKey[author.uid] && ('peergroup' in users.byKey[author.uid]))
)

const feedAllTagsSelector = createSelector(
  feedDataSelector,
  ({ allTags }) => allTags
)

export const feedTagsBySearchSelector = createSelector(
  feedDataSelector,
  ({ tagsBySearch }) => tagsBySearch
)

export const createFeedTagSelector = () => createSelector(
  feedAllTagsSelector,
  (_state: State, ownProps: FeedTagResource) => ownProps.name,
  (tags, name) => {
    if (!tags) {
      return null
    }
    return getItemFromCollection<FeedTag, 'name'>(tags, name)
  }
)

export const createFeedTagFromURLSelector = () => createSelector(
  feedAllTagsSelector,
  (_state: State, ownProps: FeedTagResource) => ownProps.name,
  (tags, name) => {
    if (!tags) {
      return null
    }
    return getItemFromCollection<FeedTag, 'name'>(tags, name)
  }
)

export const createIsCurrentlySelectedTagLoaded = () => createSelector(
  createFeedTagFromURLSelector(),
  (tag) => !!tag,
)

const feedPostsSelector = createSelector(
  feedDataSelector,
  ({ allPosts }): FeedDataState['allPosts'] => allPosts
)

export type PostUidOwnProps = WithPostUid

export const createFeedPostSelector = () => {
  const ownPropsSelector = createOwnPropsSelector<PostUidOwnProps>()

  return createSelector(
    feedPostsSelector,
    ownPropsSelector,
    (posts, { postUid }) => getItemFromCollection<Post, 'uid'>(posts, postUid)
  )
}

export const createRegularPostSelector = () => createFeedPostWithTypesSelector('post', 'sponsored_post')
export const createSocialInsightSelector = () => createFeedPostWithTypesSelector('social_insight')

const createFeedPostWithTypesSelector = <TType extends Post['type']>(...types: TType[]) => {
  return createSelector(
    createFeedPostSelector(),
    (post): Extract<Post, { type: TType }> | null => {
      if (!post || !types.includes(post.type as any)) {
        return null
      }

      return post as Extract<Post, { type: TType }>
    }
  )
}

export const feedPostSelector = createSelector(
  feedPostsSelector,
  (_state: State, ownProps: PostUidOwnProps) => ownProps.postUid,
  (posts, uid) => {
    if (!posts) {
      return null
    }
    return getItemFromCollection<Post, 'uid'>(posts, uid)
  }
)


export const isOwnPostSelector = createSelector(
  feedPostSelector,
  memberFeedInfoSelector,
  (post: Post, member): boolean | null =>
    (!post || post.type === POST_TYPE_SOCIAL_INSIGHT || !member)
      ? null
      : post.authorUid === member.uid,
)

export const isCurrentlySelectedPostLoaded = createSelector(
  feedPostSelector,
  (state: State) => state.data.feed.allPosts,
  (post, posts) => Boolean(post && posts.byKey[post.uid])
)

export const feedCommentsSelector = createSelector(
  feedDataSelector,
  ({ comments }): FeedDataState['comments'] => comments
)
export type CommentUidOwnProps = {
  commentUid: CommentUid
}

export const createPostAuthorSelector = () => createSelector(
  feedUsersSelector,
  createFeedPostSelector(),
  (users, post: Post) => {
    if (!users || !post || post.type === POST_TYPE_SOCIAL_INSIGHT) {
      return null
    }

    return getItemFromCollection<FeedUser, 'uid'>(users, post.authorUid)
  }
)

export const createFeedCommentSelector = () => {
  return createSelector(
    feedCommentsSelector,
    createOwnPropsSelector<CommentUidOwnProps>(),
    (posts, { commentUid }) => getItemFromCollection(posts, commentUid)
  )
}

export const hiddenRepliesSelector = (state: State) => state.app.feed.hiddenRepliesCommentsIds

export const createHiddenRepliesSelector = () => {
  return createSelector(
    hiddenRepliesSelector,
    createOwnPropsSelector<CommentUidOwnProps>(),
    (hiddenReplies, { commentUid }) => Boolean(hiddenReplies[commentUid])
  )
}

export const createCommentThreadSelector = () => {
  return createSelector(
    createFeedCommentSelector(),
    (comment) => comment && comment.children ? comment.children : null
  )
}

const repliesSelector = (state: State) => state.app.feed.replyCommentsIds

export type RepliesMap = Partial<Record<CommentUid, true>>
export const createThreadsRepliesSelector = () => createSelector(
  createCommentThreadSelector(),
  createOwnPropsSelector<CommentUidOwnProps>(),
  repliesSelector,
  (thread, { commentUid }, replyCommentsIds): RepliesMap => {
    const replyUids = Object.keys(replyCommentsIds)
    const threadUids = [
      ...thread ? thread.results : [],
      commentUid,
    ]
    const uids = intersection(replyUids, threadUids)
    return uids.reduce<Partial<Record<CommentUid, true>>>((acc, uid) => {
      acc[uid] = true
      return acc
    }, {})
  }
)

export const createHasReplySelector = () => createSelector(
  repliesSelector,
  createOwnPropsSelector<CommentUidOwnProps>(),
  (replyCommentsIds, { commentUid }) => Boolean(replyCommentsIds[commentUid])
)

export const createCommentAuthorSelector = () => createSelector(
  feedUsersSelector,
  createFeedCommentSelector(),
  (users, comment) => {
    if (!users || !comment) {
      return null
    }

    return getItemFromCollection<FeedUser, 'uid'>(users, comment.authorUid)
  }
)

export const createFeedAllCommentUidsByPostUidSelector = () => createSelector(
  feedPostSelector,
  (post: Post): CursorPaginatable<CommentUid> | null => {
    if (!post) {
      return null
    }

    return post.comments
  }
)

export const createIsFeedUserMemberSelector = () => {
  return createSelector(
    feedUserSelector,
    memberFeedInfoSelector,
    (user: FeedUser, memberFeed) => !user || !memberFeed ? null : user.uid === memberFeed.uid
  )
}

export const isFeedUserMemberSelector = createIsFeedUserMemberSelector()


export const feedSocialProfileSelector = (state: State) =>
  state.app.feed.socialProfile

export const suggestedUsersDataSelector = (state: State) => state.data.feed.suggestedUsers
export const suggestedTagsDataSelector = (state: State) => state.data.feed.suggestedTags

const getKey = (entity: SuggestUser | SuggestTag) =>
  'username' in entity ? entity.username : entity.name


function createSuggestedEntitiesStartsFromSelector <TKind extends 'user' | 'tag'> (kind: TKind) {
  const targetSelector = (
    kind === 'user'
      ? suggestedUsersDataSelector
      : suggestedTagsDataSelector
  ) as () => WithRequestDetails<(TKind extends 'user' ? SuggestUser : SuggestTag)[]>

  return createSelector(
    targetSelector,
    (_state: State, search: string) => search,
    (response, search) => {
      const lowCaseSearch = search.toLowerCase()
      const t = response.payload
      return t.filter((entity) => getKey(entity).toLowerCase().indexOf(lowCaseSearch) === 0)
    }
  )
}

export const suggestedUsersStartsFromSelector = createSuggestedEntitiesStartsFromSelector('user')
export const suggestedTagsStartsFromSelector = createSuggestedEntitiesStartsFromSelector('tag')

const memoEncodeUrlParams = defaultMemoize(encodeUrlParams, shallowEqual)
const BLANK_SEARCH_OBJ = { searchShadow: undefined }

export const createFeedSearchFormValuesSelector = (form?: string) => {
  if (form) {
    return (state: State) => {
      return getFormValues<{ searchShadow: string | undefined }>(form)(state) || BLANK_SEARCH_OBJ
    }
  }

  return () => BLANK_SEARCH_OBJ
}

export const createTagsByOrderingSelector = (ordering: TagsOrdering, limit?: number, form?: string) => {
  return createSelector(
    feedTagsBySearchSelector,
    feedAllTagsSelector,
    createFeedSearchFormValuesSelector(form),
    (tagsBySearch, allTags, formValues) => {
      const { searchShadow } = formValues

      const keyObj = {
        ordering,
        ...(searchShadow ? { search: searchShadow } : null),
        ...(isNumber(limit) ? { limit } : null),
      }

      const key = memoEncodeUrlParams(keyObj)

      const currentSearch = tagsBySearch[key]
      if (!currentSearch || !currentSearch.payload) {
        return null
      }

      const { isLoading, errorCode, search } = currentSearch
      const result = {
        isLoading,
        errorCode,
        search,
        payload: getItemsByKey(allTags.byKey, currentSearch.payload),
      }

      return result
    }
  )
}

export const tagsByPopularitySelector = createTagsByOrderingSelector('-popularity', FEED_POPULAR_TAGS_LIMIT)
export const tagsByTrendSelector = createTagsByOrderingSelector('-trend', FEED_TRENDING_TAGS_LIMIT)
